/**
 * The fixed-step scheduler: how world time reaches the systems.
 *
 * Systems never see frames. The world advances on an absolute grid of
 * `baseStep` world-seconds, and every system runs at its own cadence — a
 * multiple of that grid — receiving `advance(T - cadence, T)` at each grid
 * boundary T its cadence divides. Boundaries are multiples of the cadence in
 * *absolute* world time, never offsets from when advance() happened to be
 * called, so catching up a day in one call or in 1440 minute-sized calls
 * produces the identical call sequence. Determinism lives or dies here.
 */

export interface SimSystem {
  /** Advance internal state from world-time `from` to `to`, both in seconds. */
  advance(from: number, to: number): void;
}

interface Entry {
  cadence: number;
  /** Cadence as a whole number of base steps — modulo on integers, not floats. */
  mult: number;
  system: SimSystem;
}

export class Scheduler {
  private readonly baseStep: number;
  private readonly entries: Entry[] = [];

  constructor(baseStep = 60) {
    if (!Number.isFinite(baseStep) || baseStep <= 0) {
      throw new Error(`Scheduler baseStep must be positive, got ${baseStep}`);
    }
    this.baseStep = baseStep;
  }

  /**
   * Register a system to run every `cadenceSeconds`. Cadences must sit on the
   * base grid — an off-grid cadence would drift against every other system
   * and make the call sequence depend on call pattern, the one thing this
   * class exists to forbid.
   */
  add(cadenceSeconds: number, system: SimSystem): void {
    if (!Number.isFinite(cadenceSeconds) || cadenceSeconds <= 0 ||
        cadenceSeconds % this.baseStep !== 0) {
      throw new Error(
        `cadence ${cadenceSeconds} is not a positive multiple of baseStep ${this.baseStep}`);
    }
    this.entries.push({
      cadence: cadenceSeconds,
      mult: Math.round(cadenceSeconds / this.baseStep),
      system,
    });
  }

  /**
   * Run every grid boundary in (from, to], in time order; at each boundary,
   * due systems run in registration order. Empty and negative spans are
   * no-ops, so callers can hand in raw clock readings without guarding.
   */
  advance(from: number, to: number): void {
    if (!(to > from)) return;
    // first grid index strictly after `from` — multiply, never accumulate,
    // so a fractional baseStep cannot drift over long spans
    let k = Math.floor(from / this.baseStep) + 1;
    for (let t = k * this.baseStep; t <= to; t = ++k * this.baseStep) {
      for (const e of this.entries) {
        if (k % e.mult === 0) e.system.advance(t - e.cadence, t);
      }
    }
  }
}
